import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SETTINGS_ROUTE, settingsCategoryRoute, t } from '@bms2/shared';
import { PageModule } from '../../shell/PageModule';
import styles from './settings.module.css';
import { CardName, LeaveGuard } from './LeaveGuard';
import { SettingsRail, SettingsTileMenu, useSettingsTile, type SettingsTile } from './SettingsRail';

/**
 * Every Settings page follows the List Page to Record Page pattern (piece 15),
 * scoped to configuration data: one Page Module whose title is the page's Index
 * entry and whose kind line is the way back. Nothing here is a fourth page type.
 *
 * 🔒 The kind line is two links (decision 1397; T582 C2): Settings opens the
 * Index, and the category opens the Index scrolled to that category's tile.
 * The separate way back that stood on its own line above the content is gone,
 * because the eye already reads the page's place in the line under its title.
 *
 * 🔨 A shell inside a shell is a section, not a second page (decision 0584).
 * Settings entries about one subject are one page carrying a section each, so
 * the accounting basis is one door rather than three. A folded page keeps its
 * own component untouched: it renders the same shell, and the shell reads where
 * it stands.
 *
 * 🔒 Every page lists its tile's entries beside it (decision 2792): a rail at the
 * content's left from laptop width, folded into the kind line's category below it,
 * and folded at every width on a page whose content carries a rail or a wide table
 * of its own. A section draws neither, because its page already does.
 */
export interface SettingsShellProps {
  title: string;
  category: string;
  /** Where the category half of the kind line leads, when it is a page rather than the Index's tile: a vendor's steps lead back to Settings › Integrations (decision 1648). */
  categoryRoute?: string;
  newAction?: ReactNode;
  /** The title bar's own controls, a help glyph among them (decision 0708); a section draws none. */
  controls?: ReactNode;
  /** The toolbar row beneath the title bar, for a page-wide action other than + New (decision 1440; T685); a section draws it beside its heading. */
  toolbar?: ReactNode;
  /** 🔒 The tile's entries fold into the kind line at every width, for a page whose content already carries a rail or a wide table (decision 2792). */
  foldRail?: boolean;
  children: ReactNode;
}

/** A detail line assembled from parts; a call rather than a template literal, so the JSX carries no literal. */
export function line(...parts: readonly (string | number | null | undefined)[]): string {
  return parts.filter((part) => part !== null && part !== undefined && part !== '').join(' ');
}

/**
 * The anchor a folded page's section carries, or null on a page of its own. The
 * anchor is the last segment of the route that page used to have, so a redirect
 * from that route lands on the section it means and an old bookmark still works.
 */
const SectionAnchor = createContext<string | null>(null);

export function SettingsShell(props: SettingsShellProps) {
  const anchor = useContext(SectionAnchor);
  if (anchor !== null) {
    const { title, toolbar, newAction, children } = props;
    return (
      <section id={anchor} className={styles.section} data-settings-section={anchor} aria-labelledby={`${anchor}-heading`}>
        <div className={styles.sectionHeader}>
          <h2 id={`${anchor}-heading`} className={styles.sectionTitle}>{title}</h2>
          {toolbar}
          {newAction}
        </div>
        <CardName.Provider value={title}>{children}</CardName.Provider>
      </section>
    );
  }
  return <SettingsPage {...props} />;
}

/** A Settings page of its own: the Page Module, the kind line, and the tile's entries beside the content (decision 2792). */
function SettingsPage({ title, category, categoryRoute, newAction, controls, toolbar, foldRail = false, children }: SettingsShellProps) {
  const tile = useSettingsTile(category);
  const page = (
    <div className={styles.page} data-settings-page={title}>
      {/* 🔒 A typed value is not dropped silently: leaving the page asks first (decision 2781). */}
      <LeaveGuard>
        <CardName.Provider value={title}>{children}</CardName.Provider>
      </LeaveGuard>
    </div>
  );
  return (
    <PageModule title={title} icon="settings" subtitle={<SettingsKindLine category={category} tile={tile} folded={foldRail} {...(categoryRoute ? { categoryRoute } : {})} />} {...(newAction ? { newAction } : {})} {...(controls ? { controls } : {})} {...(toolbar ? { toolbar } : {})}>
      {tile && !foldRail ? (
        <div className={styles.railFrame} data-settings-rail-frame={category}>
          <SettingsRail tile={tile} />
          {page}
        </div>
      ) : (
        page
      )}
    </PageModule>
  );
}

/**
 * The kind line under a settings page's title: Settings and the category, each a
 * link back into the Index. 🔒 Where the tile's entries are folded (decision
 * 2792), the category opens them as a list instead: below laptop width on every
 * page, and at every width on a page that folds its rail.
 */
function SettingsKindLine({ category, categoryRoute, tile, folded }: { category: string; categoryRoute?: string; tile: SettingsTile | null; folded: boolean }) {
  const link = <Link className={`${styles.kindLink} ${tile ? styles.kindLinkWide : ''}`} to={categoryRoute ?? settingsCategoryRoute(category)} data-settings-crumb="category">{category}</Link>;
  return (
    <span className={styles.kindLine} data-settings-kind-line={category}>
      <Link className={styles.kindLink} to={SETTINGS_ROUTE} data-settings-crumb="index">{t('settings.title')}</Link>
      <span aria-hidden="true">{t('settings.crumbSeparator')}</span>
      {tile && folded ? null : link}
      {tile ? <SettingsTileMenu tile={tile} folded={folded} /> : null}
    </span>
  );
}

/**
 * A Settings page drawn as a section of another page, under an anchor of its own
 * (decisions 0584, 1235): the Mail Settings window draws Settings › Email's pages
 * this way, so the window and the Settings entry are the same editor.
 */
export function AsSettingsSection({ anchor, children }: { anchor: string; children: ReactNode }) {
  return <SectionAnchor.Provider value={anchor}>{children}</SectionAnchor.Provider>;
}

/** One section of a grouped page: the page that used to stand alone, under the anchor its old route named. */
export interface SettingsSection {
  /** The last segment of the route this section used to be reached at. */
  anchor: string;
  element: ReactNode;
}

export interface SettingsGroupProps {
  title: string;
  category: string;
  /** A sentence saying what the whole page holds, above the first section. */
  intro?: string;
  /** The title bar's own controls: the page's help glyph, where its sections share one topic (decision 0708). */
  controls?: ReactNode;
  sections: readonly SettingsSection[];
  /** 🔒 The tile's entries fold into the kind line at every width: Sales Tax, whose register is a wide table (decision 2792). */
  foldRail?: boolean;
}

/**
 * A Settings page whose subject several entries used to share (decision 0584):
 * one door, one breadcrumb, and a section per entry in the order a person needs
 * them. 🔨 The sections are the old pages themselves rather than copies of them,
 * so folding two entries together changes no screen's behaviour.
 *
 * The page opens on the section the address names, because the old routes
 * redirect here carrying their own anchor, and somebody following a bookmark
 * means the setting rather than the page.
 */
export function SettingsGroupPage({ title, category, intro, controls, sections, foldRail = false }: SettingsGroupProps) {
  const { hash } = useLocation();
  useEffect(() => {
    const wanted = hash.replace(/^#/, '');
    if (wanted === '') return;
    document.getElementById(wanted)?.scrollIntoView({ block: 'start' });
  }, [hash]);
  return (
    <SettingsShell title={title} category={category} foldRail={foldRail} {...(controls ? { controls } : {})}>
      {intro ? <p className={styles.hint}>{intro}</p> : null}
      {sections.map((section) => (
        <SectionAnchor.Provider key={section.anchor} value={section.anchor}>
          {section.element}
        </SectionAnchor.Provider>
      ))}
    </SettingsShell>
  );
}
